import { useState, useEffect, useMemo } from 'react'
import { X, Check, ArrowRight, AlertCircle } from 'lucide-react'
import { T, globalStyles } from '../theme'
import { formatSeasonShort } from '../context/SeasonContext'
import api from '../services/api'

/**
 * RenewSeasonModal
 * ─────────────────────────────────────────────────────────────
 * Assistant de renouvellement de l'effectif d'une saison à l'autre.
 *
 * Workflow :
 *   1. Charge les joueurs de la saison source (déjà fournis en props)
 *   2. Le coach coche ceux qu'il garde (rien coché par défaut)
 *   3. Pour chaque joueur coché, choix de la catégorie cible :
 *        - Rester (même catégorie)
 *        - +1 cran (promotion par défaut)
 *        - +2 crans (surclassement)
 *      Seniors et catégories max : seulement "Rester".
 *   4. Validation → POST /players/copy-to-season/ avec link_to_source=true
 *
 * Le backend gère la création des nouveaux joueurs avec linked_player_id
 * pour préserver l'historique multi-saisons.
 */

// ── Mapping de promotion FFF ───────────────────────────────────
// Source : nomenclature officielle FFF. Pas de notion 1ère/2ème année :
// un U17 passe U18 par défaut, le surclassement (U17 → Seniors) est une option manuelle.
const PROMOTION_MAP = {
  'U6/U7':   'U8/U9',
  'U8/U9':   'U10/U11',
  'U10/U11': 'U12/U13',
  'U12/U13': 'U14',
  'U14':     'U15',
  'U15':     'U16',
  'U16':     'U17',
  'U17':     'U18',
  'U18':     'U19',
  'U19':     'Seniors',
  'Seniors': null,    // pas de promotion
  'Vétérans': null,
}

// Pour un joueur d'une catégorie donnée, retourne les options possibles :
// - rester (même catégorie)
// - promotion par défaut (+1 cran)
// - surclassement (+2 crans, si applicable)
function getCategoryOptions(category) {
  const opts = [{ value: category, label: category, badge: 'Reste' }]
  const promo = PROMOTION_MAP[category]
  if (promo) {
    opts.push({ value: promo, label: promo, badge: 'Promotion', isDefault: true })
    const surclass = PROMOTION_MAP[promo]
    if (surclass) {
      opts.push({ value: surclass, label: surclass, badge: 'Surclassement' })
    }
  }
  return opts
}

export default function RenewSeasonModal({
  isOpen,
  onClose,
  sourceSeason,
  targetSeason,
  sourcePlayers = [],
  onSuccess,
}) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  const [selections, setSelections] = useState({}) // player_id -> { selected: bool, category: string }
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null) // { created_count, skipped_count, skipped: [...] }

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Reset quand on ouvre
  useEffect(() => {
    if (!isOpen) return
    const initial = {}
    sourcePlayers.forEach(p => {
      const promo = PROMOTION_MAP[p.category]
      initial[p.id] = {
        selected: false,
        // Cible par défaut = promotion +1 si applicable, sinon même catégorie
        category: promo || p.category,
      }
    })
    setSelections(initial)
    setError(null)
    setResult(null)
    setSubmitting(false)
  }, [isOpen, sourcePlayers])

  // useMemo avant le early return — les hooks doivent être appelés
  // dans le même ordre à chaque render, quelle que soit la valeur de isOpen.
  const playersByCategory = useMemo(() => {
    const groups = {}
    sourcePlayers.forEach(p => {
      const cat = p.category || 'Sans catégorie'
      if (!groups[cat]) groups[cat] = []
      groups[cat].push(p)
    })
    return groups
  }, [sourcePlayers])

  if (!isOpen) return null

  const selectedCount = Object.values(selections).filter(s => s.selected).length
  const allSelected = sourcePlayers.length > 0 && sourcePlayers.every(p => selections[p.id]?.selected)

  const togglePlayer = (playerId) => {
    setSelections(prev => ({
      ...prev,
      [playerId]: { ...prev[playerId], selected: !prev[playerId]?.selected },
    }))
  }

  const setPlayerCategory = (playerId, category) => {
    setSelections(prev => ({
      ...prev,
      [playerId]: { ...prev[playerId], category },
    }))
  }

  const toggleAll = () => {
    setSelections(prev => {
      const next = { ...prev }
      sourcePlayers.forEach(p => {
        next[p.id] = { ...next[p.id], selected: !allSelected }
      })
      return next
    })
  }

  const handleSubmit = async () => {
    if (selectedCount === 0) {
      setError('Sélectionne au moins un joueur à conserver pour la nouvelle saison.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const items = Object.entries(selections)
        .filter(([, s]) => s.selected)
        .map(([player_id, s]) => {
          const sourcePlayer = sourcePlayers.find(p => p.id === player_id)
          return {
            source_player_id: player_id,
            // On envoie new_category seulement si différente de la source
            new_category: s.category !== sourcePlayer?.category ? s.category : null,
          }
        })

      const { data } = await api.post('/players/copy-to-season/', {
        source_season: sourceSeason,
        target_season: targetSeason,
        players: items,
        link_to_source: true,
      })

      setResult(data)
      // On ne ferme pas tout de suite : on affiche le récap
    } catch (e) {
      const detail = e?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Une erreur est survenue lors de la copie.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (result?.created_count > 0) {
      // Si on a créé des joueurs, on prévient le parent de recharger
      onSuccess?.(result)
    }
    onClose?.()
  }

  // ── RENDU ─────────────────────────────────────────────────────

  return (
    <>
      <style>{`${globalStyles}
        @keyframes modalIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes overlayIn { from { opacity:0; } to { opacity:1; } }
      `}</style>

      {/* ─── OVERLAY ─── */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(10,9,8,0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: isMobile ? 0 : 24,
          animation: 'overlayIn .2s ease',
        }}>

        {/* ─── PANNEAU MODAL ─── */}
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: T.surface,
            width: '100%', maxWidth: 720,
            maxHeight: isMobile ? '100vh' : '90vh',
            display: 'flex', flexDirection: 'column',
            border: `1px solid ${T.rule}`,
            borderTop: `2px solid ${T.gold}`,
            animation: 'modalIn .25s ease',
          }}>

          {/* Header */}
          <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.rule}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: T.gold, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ width: 14, height: 1, background: T.gold }} />
                Renouvellement effectif
              </div>
              <h2 style={{ fontFamily: T.display, fontSize: isMobile ? 22 : 28, textTransform: 'uppercase', lineHeight: 1, color: T.ink, margin: 0, letterSpacing: '.01em' }}>
                {formatSeasonShort(sourceSeason)} <span style={{ color: T.gold }}>→</span> {formatSeasonShort(targetSeason)}
              </h2>
            </div>
            <button onClick={handleClose} style={{ background: 'none', border: `1px solid ${T.rule}`, padding: 6, cursor: 'pointer', flexShrink: 0 }}>
              <X size={14} color={T.muted} />
            </button>
          </div>

          {/* ── CORPS : récap résultat OU formulaire ── */}
          {result ? (
            // ─── ÉCRAN RÉCAP APRÈS COPIE ───
            <div style={{ padding: '32px 24px', textAlign: 'center', overflowY: 'auto' }}>
              <div style={{ width: 56, height: 56, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <Check size={28} color="#22c55e" />
              </div>
              <h3 style={{ fontFamily: T.display, fontSize: 24, textTransform: 'uppercase', color: T.ink, marginBottom: 8 }}>
                Effectif transféré.
              </h3>
              <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted, lineHeight: 1.7, marginBottom: 24 }}>
                <strong style={{ color: T.ink }}>{result.created_count}</strong> joueur{result.created_count > 1 ? 's' : ''} ajouté{result.created_count > 1 ? 's' : ''} à la saison {formatSeasonShort(targetSeason)}.
              </p>

              {result.skipped_count > 0 && (
                <div style={{ background: T.bg, border: `1px solid ${T.rule}`, padding: '14px 18px', textAlign: 'left', marginBottom: 20 }}>
                  <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AlertCircle size={12} />
                    {result.skipped_count} joueur{result.skipped_count > 1 ? 's' : ''} ignoré{result.skipped_count > 1 ? 's' : ''}
                  </div>
                  {(result.skipped || []).slice(0, 5).map((s, i) => (
                    <div key={i} style={{ fontFamily: T.mono, fontSize: 10, color: T.ink, padding: '4px 0' }}>
                      <strong>{s.name}</strong> <span style={{ color: T.muted }}>· {s.reason}</span>
                    </div>
                  ))}
                  {(result.skipped || []).length > 5 && (
                    <div style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, marginTop: 6 }}>
                      … et {result.skipped.length - 5} autre{result.skipped.length - 5 > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}

              <button onClick={handleClose} style={{
                padding: '12px 28px', background: T.gold, color: T.dark,
                fontFamily: T.mono, fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700,
                border: 'none', cursor: 'pointer',
              }}>
                Voir mon effectif {formatSeasonShort(targetSeason)} →
              </button>
            </div>
          ) : (
            // ─── ÉCRAN FORMULAIRE ───
            <>
              {/* Intro */}
              <div style={{ padding: '16px 24px', background: T.bg, borderBottom: `1px solid ${T.rule}` }}>
                <p style={{ fontFamily: T.mono, fontSize: 11, color: T.ink, lineHeight: 1.6, margin: 0 }}>
                  Coche les joueurs que tu gardes pour la saison <strong>{formatSeasonShort(targetSeason)}</strong>.
                  Pour chacun, choisis si tu le maintiens dans sa catégorie, le promeus, ou le surclasses.
                </p>
              </div>

              {/* Toggle all + compteur */}
              <div style={{ padding: '12px 24px', borderBottom: `1px solid ${T.rule}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <button onClick={toggleAll} style={{
                  padding: '6px 14px', background: 'transparent', border: `1px solid ${T.rule}`,
                  fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
                  color: T.muted, cursor: 'pointer',
                }}>
                  {allSelected ? 'Tout décocher' : 'Tout cocher'}
                </button>
                <div style={{ fontFamily: T.mono, fontSize: 10, color: T.ink, letterSpacing: '.06em' }}>
                  <strong style={{ color: T.gold, fontSize: 14, fontFamily: T.display }}>{selectedCount}</strong>
                  <span style={{ color: T.muted }}> / {sourcePlayers.length} joueur{sourcePlayers.length > 1 ? 's' : ''} sélectionné{selectedCount > 1 ? 's' : ''}</span>
                </div>
              </div>

              {/* Liste joueurs */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
                {Object.entries(playersByCategory).map(([category, players]) => (
                  <div key={category}>
                    <div style={{ padding: '10px 24px', background: T.bg, borderBottom: `1px solid ${T.rule}`, fontFamily: T.mono, fontSize: 9, letterSpacing: '.16em', textTransform: 'uppercase', color: T.gold, position: 'sticky', top: 0, zIndex: 1 }}>
                      {category} <span style={{ color: T.muted, marginLeft: 6 }}>({players.length})</span>
                    </div>
                    {players.map(player => {
                      const sel = selections[player.id] || { selected: false, category: player.category }
                      const options = getCategoryOptions(player.category)
                      return (
                        <div key={player.id} style={{
                          padding: '12px 24px',
                          borderBottom: `1px solid ${T.rule}`,
                          display: 'flex', alignItems: 'center', gap: 12,
                          background: sel.selected ? 'rgba(79,142,247,0.04)' : 'transparent',
                          transition: 'background .15s',
                        }}>
                          {/* Checkbox custom */}
                          <button
                            onClick={() => togglePlayer(player.id)}
                            style={{
                              width: 20, height: 20,
                              background: sel.selected ? T.gold : 'transparent',
                              border: `2px solid ${sel.selected ? T.gold : T.rule}`,
                              cursor: 'pointer', flexShrink: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              padding: 0,
                            }}>
                            {sel.selected && <Check size={12} color={T.dark} strokeWidth={3} />}
                          </button>

                          {/* Nom */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: T.mono, fontSize: 12, color: T.ink, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {player.name}
                            </div>
                            {player.number != null && (
                              <div style={{ fontFamily: T.mono, fontSize: 9, color: T.muted, letterSpacing: '.08em' }}>
                                N°{player.number}{player.position ? ` · ${player.position}` : ''}
                              </div>
                            )}
                          </div>

                          {/* Flèche + sélecteur catégorie cible */}
                          {sel.selected && (
                            <>
                              <ArrowRight size={12} color={T.gold} style={{ flexShrink: 0 }} />
                              <select
                                value={sel.category}
                                onChange={e => setPlayerCategory(player.id, e.target.value)}
                                style={{
                                  fontFamily: T.mono, fontSize: 10, letterSpacing: '.06em',
                                  padding: '6px 10px',
                                  background: T.surface, color: T.ink,
                                  border: `1px solid ${T.rule}`,
                                  outline: 'none', cursor: 'pointer',
                                  minWidth: isMobile ? 100 : 140,
                                  flexShrink: 0,
                                }}>
                                {options.map(opt => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label} {opt.badge && opt.value !== player.category ? `(${opt.badge})` : ''}
                                  </option>
                                ))}
                              </select>
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ))}

                {sourcePlayers.length === 0 && (
                  <div style={{ padding: '40px 24px', textAlign: 'center' }}>
                    <p style={{ fontFamily: T.mono, fontSize: 11, color: T.muted }}>
                      Aucun joueur dans la saison {formatSeasonShort(sourceSeason)}.
                    </p>
                  </div>
                )}
              </div>

              {/* Erreur */}
              {error && (
                <div style={{ padding: '12px 24px', background: 'rgba(239,68,68,0.05)', borderTop: '1px solid rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertCircle size={14} color="#ef4444" />
                  <span style={{ fontFamily: T.mono, fontSize: 10, color: '#ef4444' }}>{error}</span>
                </div>
              )}

              {/* Footer actions */}
              <div style={{ padding: '16px 24px', borderTop: `1px solid ${T.rule}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, background: T.bg }}>
                <button onClick={handleClose} disabled={submitting} style={{
                  padding: '10px 20px', background: 'transparent', color: T.muted,
                  fontFamily: T.mono, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase',
                  border: `1px solid ${T.rule}`, cursor: submitting ? 'not-allowed' : 'pointer',
                }}>
                  Annuler
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || selectedCount === 0}
                  style={{
                    padding: '11px 24px',
                    background: selectedCount === 0 ? T.rule : T.gold,
                    color: selectedCount === 0 ? T.muted : T.dark,
                    fontFamily: T.mono, fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700,
                    border: 'none',
                    cursor: submitting || selectedCount === 0 ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.6 : 1,
                  }}>
                  {submitting ? 'Transfert…' : `Transférer ${selectedCount} joueur${selectedCount > 1 ? 's' : ''} →`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
